import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle, XCircle, ChevronDown } from 'lucide-react';
import { getDecision, getRiskLevel } from './dealCalc';

const CFG = {
  pass: { color: '#45C04A', bg: 'from-[#12355B] to-[#1565C0]', border: 'border-[#1565C0]/30', Icon: CheckCircle2 },
  hold: { color: '#F59E0B', bg: 'from-[#12355B] to-[#1565C0]', border: 'border-[#1565C0]/30', Icon: AlertTriangle },
  fail: { color: '#EF4444', bg: 'from-[#12355B] to-[#1565C0]', border: 'border-[#1565C0]/30', Icon: XCircle },
};

export default function DecisionHero({ aiData, deal, onExpandAnalysis }) {
  const { t } = useTranslation();
  const scores = aiData.scores || {};
  const confidence = aiData.confidence || {};
  const decision = getDecision(scores.recommendation);
  const risk = getRiskLevel(scores.overall);
  const c = CFG[decision];

  const fair = deal.suggested_offer_price;
  const ask = deal.purchase_price;
  const diff = ask - fair;
  const pct = ((diff / ask) * 100).toFixed(1);

  return (
    <div data-testid="decision-hero" className={`relative overflow-hidden rounded-2xl border ${c.border} bg-gradient-to-br ${c.bg} p-6 md:p-8`}>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/60 mb-4">{t('decision.investmentRating')}</p>
          <div className="flex items-center gap-5 mb-4">
            <div className="relative w-[88px] h-[88px] flex-shrink-0">
              <svg className="-rotate-90 w-[88px] h-[88px]" viewBox="0 0 88 88">
                <circle cx="44" cy="44" r="36" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="5" />
                <circle cx="44" cy="44" r="36" fill="none" stroke={c.color} strokeWidth="5" strokeLinecap="round"
                  strokeDasharray={`${(scores.overall / 100) * 226} 226`}
                  style={{ filter: `drop-shadow(0 0 10px ${c.color}50)`, transition: 'stroke-dasharray 0.8s ease' }} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-[28px] font-black text-white leading-none tabular-nums">{scores.overall}</span>
                <span className="text-[8px] text-white/40 font-bold tracking-widest">/100</span>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <c.Icon className="w-5 h-5" style={{ color: c.color }} />
                <span className="text-2xl font-black tracking-tight" style={{ color: c.color }}>
                  {t(`decision.${decision}`)}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-white/10 text-white/70">
                  {t('decision.riskLevel')}: {t(`decision.${risk}Risk`)}
                </span>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-white/10 text-white/70">
                  {t('decision.confidence')}: {confidence.level}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-black/30 rounded-xl p-4 min-w-[200px] space-y-2.5">
          <Row label={t('decision.askingPrice')} value={`$${ask.toLocaleString()}`} cls="text-white" />
          <Row label={t('decision.fairValue')} value={`$${fair.toLocaleString()}`} cls="text-emerald-400" />
          <div className="border-t border-white/[0.06] pt-2">
            <Row label={t('decision.difference')}
              value={`${diff > 0 ? '+' : '-'}$${Math.abs(diff).toLocaleString()} (${pct}%)`}
              cls={diff > 0 ? 'text-red-400' : 'text-emerald-400'} />
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-400/80 mb-2">{t('aiEngine.strengths')}</p>
          {(aiData.strengths || []).slice(0, 3).map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-[13px] text-white/70 mb-1">
              <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
              {t(`aiEngine.str_${s}`, s.replace(/_/g, ' '))}
            </div>
          ))}
          {(aiData.strengths || []).length === 0 && <p className="text-xs text-white/40">{t('aiEngine.noStrengths')}</p>}
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-red-400/80 mb-2">{t('aiEngine.risks')}</p>
          {(aiData.risks || []).slice(0, 3).map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-[13px] text-white/70 mb-1">
              <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0" />
              {t(`aiEngine.risk_${r}`, r.replace(/_/g, ' '))}
            </div>
          ))}
          {(aiData.risks || []).length === 0 && <p className="text-xs text-white/40">{t('aiEngine.noRisks')}</p>}
        </div>
      </div>

      {onExpandAnalysis && (
        <button data-testid="read-detailed-analysis-btn" onClick={onExpandAnalysis}
          className="mt-5 flex items-center gap-1.5 text-xs font-semibold text-white/50 hover:text-white transition-colors">
          {t('decision.readDetailedAnalysis')} <ChevronDown className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function Row({ label, value, cls }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-white/50">{label}</span>
      <span className={`font-bold tabular-nums ${cls}`}>{value}</span>
    </div>
  );
}
